import { useState, useEffect } from 'react';
import { normalizeAjaxUrl } from '@/utils/normalizeUrl';
import { motion, AnimatePresence } from 'framer-motion';
import {
  LayoutDashboard,
  BookOpen,
  Users,
  DollarSign,
  User,
  Star,
  Clock,
  GraduationCap,
  Mail,
  Phone,
  Building2,
  Edit3,
  Loader2,
  AlertCircle,
  ChevronRight,
  Trash2,
  Send,
  RotateCcw,
  CheckCircle2,
  FileEdit,
  AlertTriangle,
  Search,
  Calendar,
  Copy,
  Video
} from 'lucide-react';

type InstructorTab = 'dashboard' | 'courses' | 'students' | 'earnings' | 'zoom' | 'profile';

interface InstructorPermissions {
  allowPublishCourses: boolean;
  allowTrashCourses: boolean;
  allowChangeAuthor: boolean;
  allowResetProgress: boolean;
}

interface CourseItem {
  id: number;
  course_name: string;
  course_slug: string;
  student_count: number;
  post_id: number;
  post_status: string;
}

interface DashboardData {
  user_id: number;
  name: string;
  email: string;
  avatar: string;
  phone: string;
  subject: string;
  institution: string;
  designation: string;
  experience: string;
  bio: string;
  rating: string;
  reviewCount: string;
  facebook: string;
  youtube: string;
  linkedin: string;
  status: string;
  totalStudents: number;
  totalCourses: number;
  revenueShare: number;
  totalEarnings: number;
  courses: CourseItem[];
  joinedAt: string;
  permissions: InstructorPermissions;
}

const tabs: { id: InstructorTab; label: string; icon: React.ElementType }[] = [
  { id: 'dashboard', label: 'ড্যাশবোর্ড', icon: LayoutDashboard },
  { id: 'courses', label: 'আমার কোর্সসমূহ', icon: BookOpen },
  { id: 'students', label: 'শিক্ষার্থী', icon: Users },
  { id: 'earnings', label: 'আয়', icon: DollarSign },
  { id: 'zoom', label: 'জুম ক্লাস', icon: Video },
  { id: 'profile', label: 'প্রোফাইল', icon: User },
];

// Mock data for dev mode
const mockDashboard: DashboardData = {
  user_id: 999,
  name: 'রাহুল আহমেদ',
  email: 'rahul@example.com',
  avatar: '',
  phone: '01711111111',
  subject: 'physics',
  institution: 'ঢাকা কলেজ',
  designation: 'সিনিয়র লেকচারার',
  experience: '5-10',
  bio: 'পদার্থবিজ্ঞানে ১০ বছরের অভিজ্ঞতাসম্পন্ন শিক্ষক। এসএসসি ও এইচএসসি শিক্ষার্থীদের পদার্থবিজ্ঞান পড়ানোতে বিশেষজ্ঞ।',
  rating: '৪.৮',
  reviewCount: '৪৫',
  facebook: '#',
  youtube: '#',
  linkedin: '#',
  status: 'approved',
  totalStudents: 342,
  totalCourses: 3,
  revenueShare: 70,
  totalEarnings: 15400,
  courses: [
    { id: 1, course_name: 'এসএসসি ২০২৭ - পদার্থবিজ্ঞান', course_slug: 'ssc-2027-physics', student_count: 156, post_id: 101, post_status: 'publish' },
    { id: 2, course_name: 'এসএসসি ২০২৭ - রসায়ন', course_slug: 'ssc-2027-chemistry', student_count: 112, post_id: 102, post_status: 'draft' },
    { id: 3, course_name: 'অষ্টম শ্রেণি - বিজ্ঞান', course_slug: 'class-8-science', student_count: 74, post_id: 103, post_status: 'pending' },
  ],
  joinedAt: 'Jan 15, 2026',
  permissions: {
    allowPublishCourses: true,
    allowTrashCourses: true,
    allowChangeAuthor: false,
    allowResetProgress: true,
  },
};

function toBengaliNumber(num: number): string {
  const en = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'];
  const bn = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  let str = num.toLocaleString('en-US');
  en.forEach((e, i) => { str = str.replace(new RegExp(e, 'g'), bn[i]); });
  return str;
}

export default function InstructorDashboardView() {
  const [activeTab, setActiveTab] = useState<InstructorTab>('dashboard');
  const [data, setData] = useState<DashboardData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    setLoading(true);
    setError('');
    try {
      const regObj = (window as any).shInstructorReg;
      if (regObj && regObj.ajax_url && regObj.nonce) {
        const formData = new FormData();
        formData.append('action', 'sh_instructor_dashboard_data');
        formData.append('nonce', regObj.nonce);
        const response = await fetch(normalizeAjaxUrl(regObj.ajax_url), { method: 'POST', body: formData });
        const res = await response.json();
        if (res.success) {
          const rawData = res.data;
          if (!rawData.permissions) {
            rawData.permissions = {
              allowPublishCourses: true,
              allowTrashCourses: true,
              allowChangeAuthor: false,
              allowResetProgress: true,
            };
          }
          rawData.courses = (rawData.courses || []).map((c: any) => ({
            ...c,
            post_id: c.post_id || c.id,
            post_status: c.post_status || 'publish',
            student_count: parseInt(c.student_count) || 0,
          }));
          setData(rawData);
        } else {
          setError(res.data?.message || (typeof res.data === 'string' ? res.data : 'ড্যাশবোর্ড ডেটা লোড করা যায়নি।'));
        }
      } else {
        await new Promise(r => setTimeout(r, 500));
        setData(mockDashboard);
      }
    } catch {
      setError('সার্ভারের সাথে যোগাযোগ করা যায়নি।');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen pb-24 space-y-8 animate-pulse text-left">
        {/* Header Section */}
        <div className="flex items-center justify-between">
          <div className="space-y-3">
            <div className="w-48 h-8 bg-slate-200 dark:bg-slate-800 rounded-2xl" />
            <div className="w-32 h-4 bg-slate-100 dark:bg-slate-800/50 rounded-xl" />
          </div>
          <div className="w-12 h-12 bg-slate-200 dark:bg-slate-800 rounded-full" />
        </div>

        {/* Tab Selection */}
        <div className="flex gap-2 overflow-x-auto pb-2 border-b border-gray-100 dark:border-slate-800">
          {[1, 2, 3, 4, 5, 6].map(i => (
            <div key={i} className="w-28 h-10 bg-slate-100 dark:bg-slate-800/60 rounded-xl flex-shrink-0" />
          ))}
        </div>

        {/* Stat Cards Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[1, 2, 3, 4].map(i => (
            <div key={i} className="h-28 bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-5" />
          ))}
        </div>

        {/* Course Rows */}
        <div className="space-y-4 mt-6">
          <div className="w-36 h-6 bg-slate-200 dark:bg-slate-800 rounded-xl" />
          {[1, 2, 3].map(i => (
            <div key={i} className="h-16 bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-2xl p-4 flex items-center justify-between" />
          ))}
        </div>
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4">
        <AlertCircle className="w-12 h-12 text-red-400" />
        <p className="text-sm text-gray-400">{error || 'ড্যাশবোর্ড ডেটা পাওয়া যায়নি।'}</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen pb-24">
      <div className="mb-8">
        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20 flex items-center justify-center">
            <GraduationCap className="w-7 h-7 text-emerald-450" />
          </div>
          <div>
            <h1 className="text-xl font-extrabold text-gray-900 dark:text-white">শিক্ষক ড্যাশবোর্ড</h1>
            <p className="text-xs text-gray-500">স্বাগতম, {data.name}</p>
          </div>
        </div>
      </div>

      <div className="flex gap-1 mb-8 overflow-x-auto pb-2 scrollbar-hide">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-xs font-medium whitespace-nowrap transition-all ${
                isActive
                  ? 'bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20 dark:border-emerald-500/30'
                  : 'bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-gray-400 border border-slate-200/50 dark:border-white/5 hover:bg-slate-200 dark:hover:bg-white/10 hover:text-slate-850 dark:hover:text-gray-300'
              }`}
            >
              <Icon className="w-4 h-4" />
              {tab.label}
            </button>
          );
        })}
      </div>

      <motion.div key={activeTab} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.25 }}>
        {activeTab === 'dashboard' && <DashboardTab data={data} />}
        {activeTab === 'courses' && <CoursesTab data={data} onRefresh={fetchDashboardData} />}
        {activeTab === 'students' && <StudentsTab data={data} />}
        {activeTab === 'earnings' && <EarningsTab data={data} />}
        {activeTab === 'zoom' && <ZoomTab data={data} />}
        {activeTab === 'profile' && <ProfileTab data={data} />}
      </motion.div>
    </div>
  );
}

/* ============ Dashboard Tab ============ */
function DashboardTab({ data }: { data: DashboardData }) {
  const stats = [
    { label: 'মোট শিক্ষার্থী', value: toBengaliNumber(data.totalStudents), icon: Users, color: 'from-blue-500/20 to-indigo-500/20', border: 'border-blue-500/20', text: 'text-blue-400' },
    { label: 'মোট কোর্স', value: toBengaliNumber(data.totalCourses), icon: BookOpen, color: 'from-emerald-500/20 to-teal-500/20', border: 'border-emerald-500/20', text: 'text-emerald-400' },
    { label: 'মোট আয়', value: '৳' + toBengaliNumber(data.totalEarnings), icon: DollarSign, color: 'from-amber-500/20 to-orange-500/20', border: 'border-amber-500/20', text: 'text-amber-400' },
    { label: 'রেটিং', value: data.rating, icon: Star, color: 'from-purple-500/20 to-pink-500/20', border: 'border-purple-500/20', text: 'text-purple-400' },
  ];

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {stats.map((stat, i) => {
          const Icon = stat.icon;
          return (
            <motion.div
              key={stat.label}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              className={`p-5 rounded-2xl bg-gradient-to-br ${stat.color} border ${stat.border} backdrop-blur-sm shadow-sm dark:shadow-none`}
            >
              <Icon className={`w-5 h-5 ${stat.text} mb-3`} />
              <p className="text-2xl font-extrabold text-slate-800 dark:text-white">{stat.value}</p>
              <p className="text-xs text-slate-650 dark:text-gray-400 mt-1">{stat.label}</p>
            </motion.div>
          );
        })}
      </div>

      <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-2xl p-6 shadow-sm dark:shadow-none">
        <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4 flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-emerald-450" />
          আমার কোর্সসমূহ
        </h3>
        <div className="space-y-3">
          {data.courses.map((course) => (
            <div key={course.id} className="flex items-center justify-between p-3 rounded-xl bg-slate-50/50 dark:bg-white/[0.02] border border-slate-100 dark:border-white/5 hover:bg-slate-100/50 dark:hover:bg-white/[0.05] transition-all">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-xl bg-emerald-500/10 border border-emerald-500/20 flex items-center justify-center">
                  <BookOpen className="w-5 h-5 text-emerald-450" />
                </div>
                <div>
                  <p className="text-sm font-medium text-slate-800 dark:text-white">{course.course_name}</p>
                  <div className="flex items-center gap-2 mt-0.5">
                    <p className="text-xs text-gray-500">{toBengaliNumber(course.student_count)} জন শিক্ষার্থী</p>
                    <StatusBadge status={course.post_status} />
                  </div>
                </div>
              </div>
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </div>
          ))}
          {data.courses.length === 0 && (
            <p className="text-xs text-gray-500 text-center py-6">কোনো কোর্স এখনো অ্যাসাইন করা হয়নি।</p>
          )}
        </div>
      </div>
    </div>
  );
}

/* ============ Status Badge ============ */
function StatusBadge({ status }: { status: string }) {
  const config: Record<string, { label: string; bg: string; text: string; icon: React.ElementType }> = {
    publish: { label: 'প্রকাশিত', bg: 'bg-emerald-500/10', text: 'text-emerald-400', icon: CheckCircle2 },
    draft: { label: 'খসড়া', bg: 'bg-amber-500/10', text: 'text-amber-400', icon: FileEdit },
    pending: { label: 'পেন্ডিং', bg: 'bg-orange-500/10', text: 'text-orange-400', icon: Clock },
  };
  const c = config[status] || config.draft;
  const Icon = c.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[9px] font-bold ${c.bg} ${c.text}`}>
      <Icon className="w-2.5 h-2.5" />
      {c.label}
    </span>
  );
}

/* ============ Courses Tab ============ */
function CoursesTab({ data, onRefresh }: { data: DashboardData; onRefresh: () => void }) {
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<number | null>(null);
  const perms = data.permissions;

  const getAjaxConfig = () => {
    const regObj = (window as any).shInstructorReg;
    const adminObj = (window as any).chorchaAdmin;
    return {
      ajaxUrl: regObj?.ajax_url || adminObj?.ajax_url || '/wp-admin/admin-ajax.php',
      nonce: adminObj?.nonce || regObj?.nonce || '',
    };
  };

  const handleStatusChange = async (postId: number, newStatus: 'publish' | 'draft' | 'pending') => {
    setActionLoading(`status-${postId}`);
    try {
      const { ajaxUrl, nonce } = getAjaxConfig();
      const formData = new FormData();
      formData.append('action', 'hub_save_or_update_course');
      formData.append('nonce', nonce);
      formData.append('post_id', postId.toString());
      const course = data.courses.find(c => c.post_id === postId);
      formData.append('title', course?.course_name || '');
      formData.append('status', newStatus === 'publish' ? 'Publish' : 'Draft');
      
      const resp = await fetch(normalizeAjaxUrl(ajaxUrl), { method: 'POST', body: formData });
      const res = await resp.json();
      if (res.success) {
        onRefresh();
      } else {
        alert(res.data || 'অপারেশন ব্যর্থ হয়েছে।');
      }
    } catch {
      alert('সার্ভারের সাথে যোগাযোগ করা যায়নি।');
    } finally {
      setActionLoading(null);
    }
  };

  const handleDelete = async (postId: number) => {
    setActionLoading(`delete-${postId}`);
    try {
      const { ajaxUrl, nonce } = getAjaxConfig();
      const formData = new FormData();
      formData.append('action', 'hub_trash_course');
      formData.append('nonce', nonce);
      formData.append('post_id', postId.toString());
      
      const resp = await fetch(normalizeAjaxUrl(ajaxUrl), { method: 'POST', body: formData });
      const res = await resp.json();
      if (res.success) {
        setConfirmDelete(null);
        onRefresh();
      } else {
        alert(res.data || 'ডিলিট করা যায়নি।');
      }
    } catch {
      alert('সার্ভারের সাথে যোগাযোগ করা যায়নি।');
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-bold text-slate-800 dark:text-white">আমার কোর্সসমূহ ({toBengaliNumber(data.totalCourses)})</h3>
      </div>
      <div className="grid gap-4 md:grid-cols-2">
        {data.courses.map((course, i) => (
          <motion.div
            key={course.id}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: i * 0.1 }}
            className="p-5 rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 hover:border-emerald-500/20 dark:hover:border-emerald-500/45 transition-all shadow-sm dark:shadow-none"
          >
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20 flex items-center justify-center shrink-0">
                <BookOpen className="w-6 h-6 text-emerald-455" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-sm font-bold text-slate-800 dark:text-white truncate">{course.course_name}</h4>
                <p className="text-xs text-gray-500 mt-1">/{course.course_slug}</p>
              </div>
            </div>
            <div className="mt-4 flex items-center gap-4">
              <div className="flex items-center gap-1.5 text-xs text-gray-400">
                <Users className="w-3.5 h-3.5" />
                {toBengaliNumber(course.student_count)} জন
              </div>
              <StatusBadge status={course.post_status} />
            </div>

            {/* Action Buttons */}
            <div className="mt-4 pt-3 border-t border-slate-100 dark:border-white/5 flex flex-wrap gap-2">
              {course.post_status !== 'publish' && (
                <button
                  onClick={() => handleStatusChange(course.post_id, perms.allowPublishCourses ? 'publish' : 'pending')}
                  disabled={actionLoading === `status-${course.post_id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-emerald-500/10 text-emerald-400 border border-emerald-500/20 hover:bg-emerald-500/20 transition-all cursor-pointer disabled:opacity-50"
                >
                  {actionLoading === `status-${course.post_id}` ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : perms.allowPublishCourses ? (
                    <Send className="w-3 h-3" />
                  ) : (
                    <Clock className="w-3 h-3" />
                  )}
                  {perms.allowPublishCourses ? 'পাবলিশ' : 'রিভিউয়ের জন্য সাবমিট'}
                </button>
              )}

              {course.post_status === 'publish' && (
                <button
                  onClick={() => handleStatusChange(course.post_id, 'draft')}
                  disabled={actionLoading === `status-${course.post_id}`}
                  className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-amber-500/10 text-amber-400 border border-amber-500/20 hover:bg-amber-500/20 transition-all cursor-pointer disabled:opacity-50"
                >
                  {actionLoading === `status-${course.post_id}` ? (
                    <Loader2 className="w-3 h-3 animate-spin" />
                  ) : (
                    <FileEdit className="w-3 h-3" />
                  )}
                  খসড়ায় ফেরত
                </button>
              )}

              {perms.allowTrashCourses && (
                <>
                  {confirmDelete === course.post_id ? (
                    <div className="flex items-center gap-1.5">
                      <button
                        onClick={() => handleDelete(course.post_id)}
                        disabled={actionLoading === `delete-${course.post_id}`}
                        className="flex items-center gap-1 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition-all cursor-pointer disabled:opacity-50"
                      >
                        {actionLoading === `delete-${course.post_id}` ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <AlertTriangle className="w-3 h-3" />
                        )}
                        নিশ্চিত
                      </button>
                      <button
                        onClick={() => setConfirmDelete(null)}
                        className="px-2 py-1.5 rounded-lg text-[11px] font-bold text-gray-500 hover:text-slate-800 dark:hover:text-gray-300 transition-all cursor-pointer border-0 bg-transparent"
                      >
                        বাতিল
                      </button>
                    </div>
                  ) : (
                    <button
                      onClick={() => setConfirmDelete(course.post_id)}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-bold bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-gray-500 border border-slate-200/50 dark:border-white/5 hover:bg-red-500/10 hover:text-red-650 dark:hover:text-red-400 hover:border-red-500/20 transition-all cursor-pointer"
                    >
                      <Trash2 className="w-3 h-3" />
                      ডিলিট
                    </button>
                  )}
                </>
              )}
            </div>
          </motion.div>
        ))}
      </div>
      {data.courses.length === 0 && (
        <div className="text-center py-16">
          <BookOpen className="w-12 h-12 text-gray-700 mx-auto mb-3" />
          <p className="text-sm text-gray-500">কোনো কোর্স এখনো অ্যাসাইন করা হয়নি।</p>
          <p className="text-xs text-gray-600 mt-1">অ্যাডমিন আপনাকে কোর্স অ্যাসাইন করলে এখানে দেখাবে।</p>
        </div>
      )}
    </div>
  );
}

/* ============ Students Tab ============ */
function StudentsTab({ data }: { data: DashboardData }) {
  const [resetLoading, setResetLoading] = useState<number | null>(null);
  const [confirmReset, setConfirmReset] = useState<number | null>(null);
  const [resetSuccess, setResetSuccess] = useState<number | null>(null);
  const perms = data.permissions;

  const handleResetProgress = async (courseId: number) => {
    setResetLoading(courseId);
    try {
      const regObj = (window as any).shInstructorReg;
      const adminObj = (window as any).chorchaAdmin;
      const ajaxUrl = regObj?.ajax_url || adminObj?.ajax_url || '/wp-admin/admin-ajax.php';
      const security = adminObj?.nonce || regObj?.nonce || '';
      
      const formData = new FormData();
      formData.append('action', 'sh_reset_student_progress');
      formData.append('security', security);
      formData.append('student_id', courseId.toString());
      
      const resp = await fetch(normalizeAjaxUrl(ajaxUrl), { method: 'POST', body: formData });
      const res = await resp.json();
      if (res.success) {
        setResetSuccess(courseId);
        setConfirmReset(null);
        setTimeout(() => setResetSuccess(null), 3000);
      } else {
        alert(res.data?.message || 'রিসেট করা যায়নি।');
      }
    } catch {
      alert('সার্ভারের সাথে যোগাযোগ করা যায়নি।');
    } finally {
      setResetLoading(null);
    }
  };

  return (
    <div className="space-y-4">
      <h3 className="text-sm font-bold text-slate-800 dark:text-white">শিক্ষার্থী তালিকা</h3>
      <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-2xl overflow-hidden shadow-sm dark:shadow-none">
        <table className="w-full">
          <thead>
            <tr className="border-b border-slate-100 dark:border-white/5">
              <th className="text-left px-5 py-3 text-xs font-medium text-gray-500">কোর্সের নাম</th>
              <th className="text-right px-5 py-3 text-xs font-medium text-gray-500">শিক্ষার্থী সংখ্যা</th>
              {perms.allowResetProgress && (
                <th className="text-right px-5 py-3 text-xs font-medium text-gray-500">অ্যাকশন</th>
              )}
            </tr>
          </thead>
          <tbody>
            {data.courses.map((course) => (
              <tr key={course.id} className="border-b border-slate-100 dark:border-white/5 last:border-0 hover:bg-slate-50/50 dark:hover:bg-white/[0.02] transition-colors">
                <td className="px-5 py-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center">
                      <Users className="w-4 h-4 text-blue-400" />
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-sm text-slate-800 dark:text-white">{course.course_name}</span>
                      <StatusBadge status={course.post_status} />
                    </div>
                  </div>
                </td>
                <td className="px-5 py-4 text-right">
                  <span className="text-sm font-bold text-slate-800 dark:text-white">{toBengaliNumber(course.student_count)}</span>
                  <span className="text-xs text-gray-500 ml-1">জন</span>
                </td>
                {perms.allowResetProgress && (
                  <td className="px-5 py-4 text-right">
                    <AnimatePresence mode="wait">
                      {resetSuccess === course.id ? (
                        <motion.span
                          key="success"
                          initial={{ opacity: 0, scale: 0.8 }}
                          animate={{ opacity: 1, scale: 1 }}
                          exit={{ opacity: 0 }}
                          className="inline-flex items-center gap-1 text-[10px] font-bold text-emerald-400"
                        >
                          <CheckCircle2 className="w-3 h-3" />
                          রিসেট হয়েছে
                        </motion.span>
                      ) : confirmReset === course.id ? (
                        <motion.div
                          key="confirm"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          className="flex items-center gap-1.5 justify-end"
                        >
                          <button
                            onClick={() => handleResetProgress(course.id)}
                            disabled={resetLoading === course.id}
                            className="flex items-center gap-1 px-2 py-1 rounded-lg text-[10px] font-bold bg-red-500/15 text-red-400 border border-red-500/20 hover:bg-red-500/25 transition-all cursor-pointer disabled:opacity-50"
                          >
                            {resetLoading === course.id ? (
                              <Loader2 className="w-2.5 h-2.5 animate-spin" />
                            ) : (
                              <AlertTriangle className="w-2.5 h-2.5" />
                            )}
                            নিশ্চিত
                          </button>
                          <button
                            onClick={() => setConfirmReset(null)}
                            className="text-[10px] text-gray-500 hover:text-slate-800 dark:hover:text-gray-300 cursor-pointer border-0 bg-transparent"
                          >
                            বাতিল
                          </button>
                        </motion.div>
                      ) : (
                        <motion.button
                          key="button"
                          initial={{ opacity: 0 }}
                          animate={{ opacity: 1 }}
                          onClick={() => setConfirmReset(course.id)}
                          className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-[10px] font-bold bg-slate-100 dark:bg-white/5 text-slate-500 dark:text-gray-500 border border-slate-200/50 dark:border-white/5 hover:bg-amber-500/10 hover:text-amber-600 dark:hover:text-amber-400 hover:border-amber-500/20 transition-all cursor-pointer ml-auto"
                        >
                          <RotateCcw className="w-2.5 h-2.5" />
                          রিসেট
                        </motion.button>
                      )}
                    </AnimatePresence>
                  </td>
                )}
              </tr>
            ))}
          </tbody>
          <tfoot>
             <tr className="bg-slate-50/50 dark:bg-white/[0.02]">
              <td className="px-5 py-3 text-xs font-bold text-gray-500">মোট</td>
              <td className="px-5 py-3 text-right text-sm font-extrabold text-emerald-600 dark:text-emerald-400">{toBengaliNumber(data.totalStudents)} জন</td>
              {perms.allowResetProgress && <td></td>}
            </tr>
          </tfoot>
        </table>
      </div>
    </div>
  );
}

/* ============ Earnings Tab ============ */
function EarningsTab({ data }: { data: DashboardData }) {
  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="p-5 rounded-2xl bg-gradient-to-br from-emerald-500/10 to-teal-500/10 border border-emerald-500/20">
          <p className="text-xs text-gray-500 mb-2">মোট আয়</p>
          <p className="text-2xl font-extrabold text-slate-800 dark:text-white">৳{toBengaliNumber(data.totalEarnings)}</p>
        </div>
        <div className="p-5 rounded-2xl bg-gradient-to-br from-blue-500/10 to-indigo-500/10 border border-blue-500/20">
          <p className="text-xs text-gray-500 mb-2">রেভিনিউ শেয়ার</p>
          <p className="text-2xl font-extrabold text-slate-800 dark:text-white">{toBengaliNumber(data.revenueShare)}%</p>
        </div>
        <div className="p-5 rounded-2xl bg-gradient-to-br from-amber-500/10 to-orange-500/10 border border-amber-500/20">
          <p className="text-xs text-gray-500 mb-2">উত্তোলনযোগ্য</p>
          <p className="text-2xl font-extrabold text-slate-800 dark:text-white">৳{toBengaliNumber(Math.floor(data.totalEarnings * data.revenueShare / 100))}</p>
        </div>
      </div>
      <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-2xl p-6 shadow-sm dark:shadow-none">
        <h3 className="text-sm font-bold text-slate-800 dark:text-white mb-4">লেনদেনের ইতিহাস</h3>
        <div className="text-center py-10">
          <DollarSign className="w-10 h-10 text-gray-400 dark:text-gray-700 mx-auto mb-3" />
          <p className="text-xs text-gray-550">এখনো কোনো লেনদেন হয়নি।</p>
        </div>
      </div>
    </div>
  );
}

/* ============ Zoom Tab ============ */
interface ZoomMeetingItem {
  id: number;
  title: string;
  meetingId: string;
  course: string;
  date: string;
  time: string;
  status: 'Live' | 'Upcoming' | 'Expired';
  joinUrl: string;
}

function ZoomTab({ data }: { data: DashboardData }) {
  const [meetingsList, setMeetingsList] = useState<ZoomMeetingItem[]>(() => {
    return [
      {
        id: 1,
        title: "Live Q&A: " + (data.courses[0]?.course_name || "গণিত লাইভ ক্লাস"),
        meetingId: "123 456 7890",
        course: data.courses[0]?.course_name || "এসএসসি ২০২৭ - গণিত",
        date: "2026-07-20",
        time: "10:00 AM - 11:30 AM",
        status: "Live",
        joinUrl: "https://zoom.us/j/1234567890",
      },
      {
        id: 2,
        title: "পদার্থবিজ্ঞান - জটিল সৃজনশীল সমাধান ক্লাস",
        meetingId: "098 765 4321",
        course: data.courses[1]?.course_name || data.courses[0]?.course_name || "এসএসসি ২০২৭ - পদার্থবিজ্ঞান",
        date: "2026-07-22",
        time: "02:00 PM - 03:30 PM",
        status: "Upcoming",
        joinUrl: "https://zoom.us/j/0987654321",
      },
      {
        id: 3,
        title: "অষ্টম শ্রেণি বিজ্ঞান - প্রজেক্ট ও রিভিশন",
        meetingId: "567 890 1234",
        course: data.courses[2]?.course_name || data.courses[0]?.course_name || "অষ্টম শ্রেণি - বিজ্ঞান",
        date: "2026-07-18",
        time: "11:00 AM - 12:00 PM",
        status: "Expired",
        joinUrl: "https://zoom.us/j/5678901234",
      },
    ];
  });

  const [activeSubTab, setActiveSubTab] = useState<'all' | 'live' | 'upcoming' | 'expired'>('all');
  const [searchQuery, setSearchQuery] = useState("");
  const [copiedId, setCopiedId] = useState<number | null>(null);

  // Form State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formTitle, setFormTitle] = useState("");
  const [formCourse, setFormCourse] = useState(data.courses[0]?.course_name || "");
  const [formDate, setFormDate] = useState("");
  const [formTimeStart, setFormTimeStart] = useState("");
  const [formTimeEnd, setFormTimeEnd] = useState("");
  const [formJoinUrl, setFormJoinUrl] = useState("https://zoom.us/j/");

  const handleCopy = (id: number, meetingId: string) => {
    navigator.clipboard.writeText(meetingId.replace(/\s+/g, ''));
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleDelete = (id: number) => {
    if (confirm("আপনি কি নিশ্চিতভাবে এই জুম মিটিংটি মুছে ফেলতে চান?")) {
      setMeetingsList(prev => prev.filter(m => m.id !== id));
    }
  };

  const handleCreateMeeting = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formTitle || !formDate || !formTimeStart || !formTimeEnd) {
      alert("অনুগ্রহ করে সব তথ্য পূরণ করুন।");
      return;
    }

    const start = formatAMPM(formTimeStart);
    const end = formatAMPM(formTimeEnd);
    const generatedId = Math.floor(100000000 + Math.random() * 900000000).toString().replace(/(\d{3})(\d{3})(\d{3})/, '$1 $2 $3');

    const newMeeting: ZoomMeetingItem = {
      id: Date.now(),
      title: formTitle,
      meetingId: generatedId,
      course: formCourse,
      date: formDate,
      time: `${start} - ${end}`,
      status: 'Upcoming',
      joinUrl: formJoinUrl || `https://zoom.us/j/${generatedId.replace(/\s+/g, '')}`,
    };

    setMeetingsList(prev => [newMeeting, ...prev]);
    setIsModalOpen(false);

    // Reset Form
    setFormTitle("");
    setFormDate("");
    setFormTimeStart("");
    setFormTimeEnd("");
    setFormJoinUrl("https://zoom.us/j/");
  };

  const formatAMPM = (timeStr: string) => {
    const [hoursStr, minutesStr] = timeStr.split(':');
    let hours = parseInt(hoursStr);
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12; // the hour '0' should be '12'
    return `${hours}:${minutesStr} ${ampm}`;
  };

  const filteredMeetings = meetingsList.filter(m => {
    const matchesTab = activeSubTab === 'all' ? true : m.status.toLowerCase() === activeSubTab;
    const matchesSearch = 
      m.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.course.toLowerCase().includes(searchQuery.toLowerCase()) ||
      m.meetingId.includes(searchQuery);
    return matchesTab && matchesSearch;
  });

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h2 className="text-lg font-bold text-slate-850 dark:text-white">লাইভ জুম ক্লাস ও মিটিং</h2>
          <p className="text-xs text-gray-500 mt-1">আপনার শিক্ষার্থীদের জন্য সরাসরি জুম মিটিং তৈরি ও পরিচালনা করুন।</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="flex items-center justify-center gap-1.5 px-4 py-2.5 rounded-xl text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-colors shadow-lg cursor-pointer"
        >
          <Video className="w-4 h-4" />
          মিটিং তৈরি করুন
        </button>
      </div>

      {/* Tabs and Search */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 bg-white dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 p-4 rounded-2xl shadow-sm dark:shadow-none">
        <div className="flex items-center gap-2 overflow-x-auto pb-1 scrollbar-hide">
          {(['all', 'live', 'upcoming', 'expired'] as const).map(tabKey => {
            const count = tabKey === 'all' ? meetingsList.length : meetingsList.filter(m => m.status.toLowerCase() === tabKey).length;
            const labelMap = { all: 'সব', live: 'লাইভ', upcoming: 'আসন্ন', expired: 'অতিক্রান্ত' };
            const isActive = activeSubTab === tabKey;
            return (
              <button
                key={tabKey}
                onClick={() => setActiveSubTab(tabKey)}
                className={`px-3.5 py-1.5 rounded-lg text-xs font-semibold transition-all whitespace-nowrap cursor-pointer ${
                  isActive
                    ? 'bg-emerald-500/10 dark:bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                    : 'bg-slate-100 dark:bg-white/5 text-slate-650 dark:text-gray-400 border border-transparent hover:bg-slate-200 dark:hover:bg-white/10 hover:text-slate-800 dark:hover:text-gray-300'
                }`}
              >
                {labelMap[tabKey]} ({toBengaliNumber(count)})
              </button>
            );
          })}
        </div>

        <div className="relative max-w-md w-full">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-500" />
          <input
            type="text"
            placeholder="মিটিং খুঁজুন..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/5 focus:border-emerald-500/30 rounded-xl text-xs text-slate-800 dark:text-white placeholder-gray-500 outline-none transition-colors"
          />
        </div>
      </div>

      {/* Meetings List */}
      <div className="grid grid-cols-1 gap-4">
        {filteredMeetings.length === 0 ? (
          <div className="bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 rounded-2xl p-12 text-center shadow-sm dark:shadow-none">
            <Video className="w-10 h-10 text-gray-400 dark:text-gray-700 mx-auto mb-3" />
            <p className="text-xs text-gray-500">কোনো জুম মিটিং পাওয়া যায়নি।</p>
          </div>
        ) : (
          filteredMeetings.map(m => {
            const isLive = m.status === 'Live';
            const isUpcoming = m.status === 'Upcoming';
            const isExpired = m.status === 'Expired';
            
            return (
              <div
                key={m.id}
                className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 p-5 rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 hover:border-slate-300 dark:hover:border-white/10 transition-colors shadow-sm dark:shadow-none"
              >
                <div className="flex items-start gap-4">
                  <div className={`w-12 h-12 rounded-2xl flex items-center justify-center shrink-0 ${
                    isLive 
                      ? 'bg-emerald-500/10 border border-emerald-500/20 text-emerald-600 dark:text-emerald-400' 
                      : isUpcoming 
                        ? 'bg-blue-500/10 border border-blue-500/20 text-blue-600 dark:text-blue-400' 
                        : 'bg-slate-100 dark:bg-white/5 border border-slate-200/50 dark:border-white/5 text-gray-500'
                  }`}>
                    <Video className="w-6 h-6" />
                  </div>
                  <div className="space-y-1.5">
                    <div className="flex flex-wrap items-center gap-2">
                      <h4 className="text-sm font-bold text-slate-800 dark:text-white">{m.title}</h4>
                      <span className={`px-2 py-0.5 rounded-full text-[9px] font-bold ${
                        isLive 
                          ? 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20' 
                          : isUpcoming 
                            ? 'bg-blue-500/15 text-blue-600 dark:text-blue-400 border border-blue-500/20' 
                            : 'bg-slate-200 dark:bg-white/10 text-gray-550 dark:text-gray-500'
                      }`}>
                        {isLive ? 'লাইভ' : isUpcoming ? 'আসন্ন' : 'অতিক্রান্ত'}
                      </span>
                    </div>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-3.5 h-3.5" />
                        {m.course}
                      </span>
                      <span className="flex items-center gap-1 cursor-pointer hover:text-slate-850 dark:hover:text-white" onClick={() => handleCopy(m.id, m.meetingId)}>
                        <Copy className="w-3.5 h-3.5" />
                        আইডি: {m.meetingId}
                        {copiedId === m.id && (
                          <span className="text-[10px] text-emerald-600 dark:text-emerald-400 font-bold ml-1">কপি করা হয়েছে!</span>
                        )}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="flex flex-col sm:flex-row sm:items-center gap-4 lg:self-center">
                  <div className="flex items-center gap-2 text-xs text-gray-500 bg-slate-50 dark:bg-white/[0.02] border border-slate-200 dark:border-white/5 px-4 py-2.5 rounded-xl">
                    <Calendar className="w-4 h-4 text-gray-400" />
                    <span>{m.date}</span>
                    <span className="text-slate-200 dark:text-white/10">|</span>
                    <Clock className="w-4 h-4 text-gray-400" />
                    <span>{m.time}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => isLive || isUpcoming ? window.open(m.joinUrl, "_blank") : null}
                      disabled={isExpired}
                      className={`flex-1 sm:flex-initial px-5 py-2.5 rounded-xl text-xs font-bold transition-all cursor-pointer ${
                        isExpired 
                          ? 'bg-slate-100 dark:bg-white/5 text-slate-405 dark:text-gray-600 border border-slate-200/50 dark:border-white/5 cursor-not-allowed' 
                          : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-md'
                      }`}
                    >
                      ক্লাসে যোগ দিন
                    </button>
                    <button
                      onClick={() => handleDelete(m.id)}
                      className="px-2.5 py-2.5 rounded-xl text-xs font-bold bg-slate-100 dark:bg-white/5 hover:bg-red-500/10 hover:text-red-650 dark:hover:text-red-400 text-gray-500 border border-slate-200/50 dark:border-white/5 hover:border-red-500/20 transition-all cursor-pointer"
                      title="মুছে ফেলুন"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            );
          })
        )}
      </div>

      {/* Create Meeting Modal Overlay */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-fade-in">
          <div className="w-full max-w-md bg-white dark:bg-brand-dark-card border border-slate-200 dark:border-white/10 rounded-2xl overflow-hidden shadow-2xl p-6 space-y-5">
            <div className="flex items-center justify-between border-b border-slate-100 dark:border-white/5 pb-3">
              <h3 className="text-base font-bold text-slate-800 dark:text-white flex items-center gap-2">
                <Video className="w-5 h-5 text-emerald-450" />
                নতুন জুম ক্লাস তৈরি করুন
              </h3>
              <button onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-slate-850 dark:hover:text-white cursor-pointer bg-transparent border-0 text-xl font-bold">×</button>
            </div>

            <form onSubmit={handleCreateMeeting} className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-slate-605 dark:text-gray-300 mb-2">মিটিংয়ের নাম/টপিক</label>
                <input
                  type="text"
                  required
                  placeholder="যেমন: অধ্যায় ৫ - গাণিতিক সমস্যা সমাধান"
                  value={formTitle}
                  onChange={(e) => setFormTitle(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:border-emerald-500/30 rounded-xl text-xs text-slate-800 dark:text-white placeholder-gray-500 outline-none transition-colors"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-605 dark:text-gray-300 mb-2">সংশ্লিষ্ট কোর্স</label>
                <select
                  value={formCourse}
                  onChange={(e) => setFormCourse(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:border-emerald-500/30 rounded-xl text-xs text-slate-800 dark:text-white outline-none transition-colors cursor-pointer"
                >
                  {data.courses.map(c => (
                    <option key={c.id} value={c.course_name} className="bg-white dark:bg-gray-900 text-slate-800 dark:text-white">{c.course_name}</option>
                  ))}
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-slate-605 dark:text-gray-300 mb-2">মিটিংয়ের তারিখ</label>
                  <input
                    type="date"
                    required
                    value={formDate}
                    onChange={(e) => setFormDate(e.target.value)}
                    className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:border-emerald-500/30 rounded-xl text-xs text-slate-800 dark:text-white outline-none transition-colors"
                  />
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-xs font-semibold text-slate-605 dark:text-gray-300 mb-2">শুরু</label>
                    <input
                      type="time"
                      required
                      value={formTimeStart}
                      onChange={(e) => setFormTimeStart(e.target.value)}
                      className="w-full px-2 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:border-emerald-500/30 rounded-xl text-[10px] text-slate-800 dark:text-white outline-none transition-colors"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-semibold text-slate-605 dark:text-gray-300 mb-2">শেষ</label>
                    <input
                      type="time"
                      required
                      value={formTimeEnd}
                      onChange={(e) => setFormTimeEnd(e.target.value)}
                      className="w-full px-2 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:border-emerald-500/30 rounded-xl text-[10px] text-slate-800 dark:text-white outline-none transition-colors"
                    />
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-605 dark:text-gray-300 mb-2">জুম জয়েন লিংক (ঐচ্ছিক)</label>
                <input
                  type="url"
                  placeholder="https://zoom.us/j/..."
                  value={formJoinUrl}
                  onChange={(e) => setFormJoinUrl(e.target.value)}
                  className="w-full px-4 py-2.5 bg-slate-50 dark:bg-white/5 border border-slate-200 dark:border-white/10 focus:border-emerald-500/30 rounded-xl text-xs text-slate-800 dark:text-white placeholder-gray-500 outline-none transition-colors"
                />
              </div>

              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-slate-100 dark:bg-white/5 text-slate-600 dark:text-gray-400 hover:text-slate-800 dark:hover:text-white border border-slate-200/50 dark:border-white/5 transition-all cursor-pointer"
                >
                  বাতিল
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2.5 rounded-xl text-xs font-bold bg-emerald-500 text-white hover:bg-emerald-600 transition-all cursor-pointer"
                >
                  তৈরি করুন
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

/* ============ Profile Tab ============ */
function ProfileTab({ data }: { data: DashboardData }) {
  const subjectLabels: Record<string, string> = {
    bangla: 'বাংলা', english: 'ইংরেজি', math: 'গণিত', physics: 'পদার্থবিজ্ঞান',
    chemistry: 'রসায়ন', biology: 'জীববিজ্ঞান', ict: 'তথ্য ও যোগাযোগ প্রযুক্তি',
    accounting: 'হিসাববিজ্ঞান', business: 'ব্যবসায় উদ্যোগ', economics: 'অর্থনীতি',
    other: 'অন্যান্য',
  };

  return (
    <div className="space-y-6">
      <div className="p-6 rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 shadow-sm dark:shadow-none">
        <div className="flex items-center gap-5">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500/20 to-teal-500/20 border border-emerald-500/20 flex items-center justify-center overflow-hidden">
            {data.avatar ? (
              <img src={data.avatar} alt={data.name} className="w-full h-full object-cover" />
            ) : (
              <User className="w-10 h-10 text-emerald-450" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-extrabold text-slate-850 dark:text-white">{data.name}</h2>
            <p className="text-xs text-gray-500 mt-0.5">{data.designation || 'শিক্ষক, শুদ্ধশিক্ষা'}</p>
            <div className="flex items-center gap-2 mt-2">
              <span className="px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 text-[10px] font-bold">অনুমোদিত</span>
              <span className="text-[10px] text-gray-500 dark:text-gray-600">যোগদান: {data.joinedAt}</span>
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-2">
        <div className="p-5 rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 space-y-4 shadow-sm dark:shadow-none">
          <h3 className="text-sm font-bold text-slate-850 dark:text-white flex items-center gap-2">
            <User className="w-4 h-4 text-emerald-450" />
            ব্যক্তিগত তথ্য
          </h3>
          <div className="space-y-3">
            <InfoRow icon={Mail} label="ইমেইল" value={data.email} />
            <InfoRow icon={Phone} label="ফোন" value={data.phone} />
            <InfoRow icon={BookOpen} label="विषয়" value={subjectLabels[data.subject] || data.subject} />
            <InfoRow icon={Building2} label="প্রতিষ্ঠান" value={data.institution} />
            <InfoRow icon={Clock} label="অভিজ্ঞতা" value={data.experience ? `${data.experience} বছর` : ''} />
          </div>
        </div>

        <div className="p-5 rounded-2xl bg-white dark:bg-white/[0.03] border border-slate-200 dark:border-white/5 space-y-4 shadow-sm dark:shadow-none">
          <h3 className="text-sm font-bold text-slate-850 dark:text-white flex items-center gap-2">
            <Edit3 className="w-4 h-4 text-emerald-450" />
            বায়ো
          </h3>
          <p className="text-xs text-slate-650 dark:text-gray-400 leading-relaxed">{data.bio || 'কোনো বায়ো যোগ করা হয়নি।'}</p>

          {(data.facebook || data.youtube || data.linkedin) && (
            <>
              <h4 className="text-xs font-bold text-gray-500 mt-4">সোশ্যাল মিডিয়া</h4>
              <div className="flex gap-2">
                {data.facebook && data.facebook !== '#' && (
                  <a href={data.facebook} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center hover:bg-blue-500/20 transition-all text-blue-600 dark:text-blue-400 text-xs font-bold">
                    f
                  </a>
                )}
                {data.youtube && data.youtube !== '#' && (
                  <a href={data.youtube} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-red-500/10 flex items-center justify-center hover:bg-red-500/20 transition-all text-red-600 dark:text-red-400 text-xs font-bold">
                    ▶
                  </a>
                )}
                {data.linkedin && data.linkedin !== '#' && (
                  <a href={data.linkedin} target="_blank" rel="noopener noreferrer" className="w-8 h-8 rounded-lg bg-sky-500/10 flex items-center justify-center hover:bg-sky-500/20 transition-all text-sky-600 dark:text-sky-400 text-xs font-bold">
                    in
                  </a>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }: { icon: React.ElementType; label: string; value: string }) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3">
      <Icon className="w-4 h-4 text-gray-400 dark:text-gray-600 shrink-0" />
      <div className="min-w-0">
        <p className="text-[10px] text-gray-405 dark:text-gray-600">{label}</p>
        <p className="text-xs text-slate-800 dark:text-gray-300 truncate">{value}</p>
      </div>
    </div>
  );
}
